import type { CoachResponse, PlayerProfileResponse } from '@on3/api';
import { On3IconVerifiedShield } from '@on3/icons';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';

import { CompactCoach } from './CompactCoach';
import styles from './CompactHeader.module.scss';
import { CompactPlayer } from './CompactPlayer';

export interface CompactHeaderProps {
  basicInfo?: PlayerProfileResponse | null;
  coachInfo?: CoachResponse | null;
  recruitingLink?: boolean;
  nilLink?: boolean;
}

export const CompactHeader = ({
  basicInfo,
  coachInfo,
  recruitingLink,
  nilLink,
}: CompactHeaderProps) => {
  const router = useRouter();
  const { id } = router.query;
  const isVerified = basicInfo?.athleteVerified;
  const isFootballAthlete = basicInfo?.defaultSport?.name === 'Football';
  const isBasketballAthlete = basicInfo?.defaultSport?.name === 'Basketball';

  return (
    <header className={styles.header}>
      <div className={styles.compactHeaderName}>
        {basicInfo && (
          <Text className={styles.heading} variant="h1">
            {basicInfo?.name ?? ''}
            {basicInfo?.name && nilLink ? ' - ' : ''}
            {nilLink ? 'NIL Deals' : ''}
          </Text>
        )}
        {coachInfo && (
          <Text className={styles.heading} variant="h1">
            {coachInfo?.fullName ?? ''}
          </Text>
        )}
        {isVerified && (
          <div className={styles.verifiedContainer}>
            <On3IconVerifiedShield
              className={styles.recruitingIcon}
              height={25}
              width={20}
            />

            <Text
              className={styles.verifiedProfileVerbiage}
              variant="subtitle1"
            >
              Elite Verified
            </Text>
          </div>
        )}
      </div>
      {basicInfo && <CompactPlayer basicInfo={basicInfo} />}
      {coachInfo && <CompactCoach coachInfo={coachInfo} />}
      <div>
        <TextLink className={styles.textLink} href="/rivals/" underline="none">
          Rivals
        </TextLink>
        /
        {basicInfo && (
          <TextLink
            className={styles.textLink}
            href={`/rivals/${id}/`}
            underline="none"
          >
            {basicInfo?.name}
          </TextLink>
        )}
        {coachInfo && (
          <TextLink
            className={styles.textLink}
            href={`/rivals/coach/${id}/`}
            underline="none"
          >
            {coachInfo?.fullName}
          </TextLink>
        )}
        {recruitingLink && (
          <>
            /
            <TextLink
              className={styles.textLink}
              href={`/rivals/${id}/recruiting/`}
              underline="none"
            >
              Recruiting
            </TextLink>
          </>
        )}
        {nilLink && (isFootballAthlete || isBasketballAthlete) && (
          <>
            /
            <TextLink
              className={styles.textLink}
              href={`/rivals/${id}/nil/`}
              underline="none"
            >
              NIL Profile
            </TextLink>
          </>
        )}
      </div>
    </header>
  );
};
